import React, { useMemo } from 'react';
import type { PublicTrustSnapshot } from '../../../types/publicTrust.ts';
import {
  buildTrustAvatarLead,
  buildTrustAvatarSlots,
  buildTrustCardHeadline,
  buildTrustCardSubhead,
  getTrustAvatarOverflowCount,
  shouldShowTrustAvatars,
} from '../../../utils/publicTrust/publicTrustCopy.ts';
import TrustAvatarSlotInteractive from './TrustAvatarSlot.tsx';
import TrustOverflowBadge from './TrustOverflowBadge.tsx';
import styles from './PublicTrustSnapshotCard.module.scss';

type PublicTrustSnapshotCardProps = {
  snapshot: PublicTrustSnapshot | null;
  isLoading?: boolean;
};

function getPopoverAlign(index: number, total: number): 'start' | 'center' | 'end' {
  if (index === 0) return 'end';
  if (index >= total - 1) return 'end';
  return 'center';
}

const PublicTrustSnapshotCard: React.FC<PublicTrustSnapshotCardProps> = ({
  snapshot,
  isLoading = false,
}) => {
  const cardModel = useMemo(() => {
    if (!snapshot) return null;

    const { stats, meta, activity } = snapshot;
    const showAvatars = shouldShowTrustAvatars(meta);
    const avatarSlots = showAvatars ? buildTrustAvatarSlots(activity) : [];
    const overflowCount = getTrustAvatarOverflowCount(stats, meta, avatarSlots.length);

    return {
      headline: buildTrustCardHeadline(stats, meta),
      subhead: buildTrustCardSubhead(stats, meta),
      avatarLead: buildTrustAvatarLead(stats, meta, activity.length),
      avatarSlots,
      overflowCount,
      showAvatars: avatarSlots.length > 0,
    };
  }, [snapshot]);

  if (isLoading && !snapshot) {
    return (
      <div className={styles.cardSkeleton} aria-busy="true" aria-label="Cargando red de profesionales">
        <div className={styles.cardMainRow}>
          <div className={styles.copySkeleton}>
            <span className={styles.lineSkeleton} />
            <span className={styles.lineSkeletonShort} />
          </div>
          <div className={styles.avatarAside}>
            <span className={styles.avatarLeadSkeleton} />
            <div className={styles.avatarRow}>
              <span className={styles.avatarSkeleton} />
              <span className={styles.avatarSkeleton} />
              <span className={styles.avatarSkeleton} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!cardModel) return null;

  return (
    <article
      className={`${styles.card} ${cardModel.showAvatars ? '' : styles.cardNoAvatars}`}
      aria-label="Red de profesionales en Edify"
    >
      <div className={styles.cardMainRow}>
        <div className={styles.copyBlock}>
          <p className={styles.headline}>{cardModel.headline}</p>
          <p className={styles.subhead}>{cardModel.subhead}</p>
        </div>

        {cardModel.showAvatars ? (
          <div className={styles.avatarAside}>
            {cardModel.avatarLead ? (
              <p className={styles.avatarLead}>{cardModel.avatarLead}</p>
            ) : null}
            <div className={styles.avatarRow}>
              <div className={styles.avatarStack}>
                {cardModel.avatarSlots.map((slot, index) => (
                  <TrustAvatarSlotInteractive
                    key={slot.key}
                    slot={slot}
                    popoverAlign={getPopoverAlign(index, cardModel.avatarSlots.length)}
                  />
                ))}
              </div>
              {cardModel.overflowCount > 0 ? (
                <TrustOverflowBadge count={cardModel.overflowCount} />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default PublicTrustSnapshotCard;
